export interface Conflict {
  id: string;
  type: 'teacher_double_booking' | 'room_double_booking' | 'group_double_booking' | 'room_capacity' | 'teacher_unavailable' | 'consecutive_limit';
  severity: 'high' | 'medium' | 'low';
  day: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday';
  period: number;
  affectedLessons: string[]; // lesson IDs
  description: string;
  suggestedResolutions: Resolution[];
}

export interface Resolution {
  id: string;
  type: 'move_lesson' | 'swap_lessons' | 'change_room' | 'change_teacher' | 'split_group';
  description: string;
  impact: 'minimal' | 'moderate' | 'significant';
  targetLessonId?: string;
  targetDay?: string;
  targetPeriod?: number;
  targetRoomId?: string;
  targetTeacherId?: string;
  confidence: number; // 0-100
}

export const mockConflicts: Conflict[] = [
  {
    id: 'conflict-001',
    type: 'teacher_double_booking',
    severity: 'high',
    day: 'monday',
    period: 1,
    affectedLessons: ['lesson-001', 'lesson-002'],
    description: 'Ms. Smith is scheduled to teach English to Year 7A and Math to Year 8B simultaneously',
    suggestedResolutions: [
      {
        id: 'res-001-1',
        type: 'move_lesson',
        description: 'Move Year 8B Math to Monday Period 3',
        impact: 'minimal',
        targetLessonId: 'lesson-002',
        targetDay: 'monday',
        targetPeriod: 3,
        confidence: 92,
      },
      {
        id: 'res-001-2',
        type: 'move_lesson',
        description: 'Move Year 7A English to Tuesday Period 1',
        impact: 'moderate',
        targetLessonId: 'lesson-001',
        targetDay: 'tuesday',
        targetPeriod: 1,
        confidence: 85,
      },
    ],
  },
  {
    id: 'conflict-002',
    type: 'room_double_booking',
    severity: 'high',
    day: 'tuesday',
    period: 2,
    affectedLessons: ['lesson-010', 'lesson-011'],
    description: 'Room S1 (Science Lab) is booked for both Chemistry and Physics classes',
    suggestedResolutions: [
      {
        id: 'res-002-1',
        type: 'change_room',
        description: 'Move Chemistry to Science Lab S2',
        impact: 'minimal',
        targetLessonId: 'lesson-010',
        targetRoomId: 'room-s2',
        confidence: 95,
      },
      {
        id: 'res-002-2',
        type: 'move_lesson',
        description: 'Move Physics to Tuesday Period 4',
        impact: 'moderate',
        targetLessonId: 'lesson-011',
        targetDay: 'tuesday',
        targetPeriod: 4,
        confidence: 78,
      },
    ],
  },
  {
    id: 'conflict-003',
    type: 'group_double_booking',
    severity: 'high',
    day: 'wednesday',
    period: 3,
    affectedLessons: ['lesson-020', 'lesson-021'],
    description: 'Year 10A is scheduled for both History and Geography at the same time',
    suggestedResolutions: [
      {
        id: 'res-003-1',
        type: 'move_lesson',
        description: 'Move History to Wednesday Period 5',
        impact: 'minimal',
        targetLessonId: 'lesson-020',
        targetDay: 'wednesday',
        targetPeriod: 5,
        confidence: 90,
      },
      {
        id: 'res-003-2',
        type: 'swap_lessons',
        description: 'Swap with Year 10B Geography (currently Period 5)',
        impact: 'moderate',
        targetLessonId: 'lesson-021',
        confidence: 82,
      },
    ],
  },
  {
    id: 'conflict-004',
    type: 'room_capacity',
    severity: 'medium',
    day: 'thursday',
    period: 1,
    affectedLessons: ['lesson-030'],
    description: 'Year 11 combined group (35 students) assigned to Room 12 (capacity: 30)',
    suggestedResolutions: [
      {
        id: 'res-004-1',
        type: 'change_room',
        description: 'Move to Main Hall (capacity: 50)',
        impact: 'minimal',
        targetLessonId: 'lesson-030',
        targetRoomId: 'room-hall',
        confidence: 88,
      },
      {
        id: 'res-004-2',
        type: 'split_group',
        description: 'Split into two smaller groups across two periods',
        impact: 'significant',
        targetLessonId: 'lesson-030',
        confidence: 65,
      },
    ],
  },
  {
    id: 'conflict-005',
    type: 'teacher_unavailable',
    severity: 'medium',
    day: 'friday',
    period: 5,
    affectedLessons: ['lesson-040'],
    description: 'Mr. Johnson scheduled for PE but has marked Friday Period 5 as unavailable',
    suggestedResolutions: [
      {
        id: 'res-005-1',
        type: 'move_lesson',
        description: 'Move PE to Friday Period 3',
        impact: 'minimal',
        targetLessonId: 'lesson-040',
        targetDay: 'friday',
        targetPeriod: 3,
        confidence: 93,
      },
      {
        id: 'res-005-2',
        type: 'change_teacher',
        description: 'Assign Ms. Davis as substitute PE teacher',
        impact: 'moderate',
        targetLessonId: 'lesson-040',
        targetTeacherId: 'teacher-davis',
        confidence: 75,
      },
    ],
  },
  {
    id: 'conflict-006',
    type: 'consecutive_limit',
    severity: 'low',
    day: 'monday',
    period: 3,
    affectedLessons: ['lesson-050'],
    description: 'Year 7C has 4 consecutive academic lessons (exceeds recommended limit of 3)',
    suggestedResolutions: [
      {
        id: 'res-006-1',
        type: 'move_lesson',
        description: 'Move one lesson to create a break',
        impact: 'minimal',
        targetLessonId: 'lesson-050',
        targetDay: 'monday',
        targetPeriod: 5,
        confidence: 80,
      },
    ],
  },
];

export const conflictTypeLabels: Record<Conflict['type'], string> = {
  teacher_double_booking: 'Teacher Double-Booking',
  room_double_booking: 'Room Double-Booking',
  group_double_booking: 'Student Group Double-Booking',
  room_capacity: 'Room Capacity Exceeded',
  teacher_unavailable: 'Teacher Unavailable',
  consecutive_limit: 'Consecutive Lesson Limit',
};

export const severityColors: Record<Conflict['severity'], string> = {
  high: 'error',
  medium: 'warning',
  low: 'info',
};
